import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, MapTrifold, Warning, PencilSimple, LockKey } from "@phosphor-icons/react";
import { formatClockTime, formatWorkDate, formatSeconds, formatAuditValue, COMPANY_TZ_LABEL } from "@/lib/time";

export const mapsUrl = (gps) => `https://www.google.com/maps?q=${gps.lat},${gps.lng}`;

const hasGps = (gps) =>
    !!gps && typeof gps.lat === "number" && typeof gps.lng === "number";

/** Compact table cell: 📍 IN / 📍 OUT links, or a neutral dash. */
export const LocationCell = ({ entry }) => {
    const inGps = hasGps(entry.clock_in_gps);
    const outGps = hasGps(entry.clock_out_gps);
    if (!inGps && !outGps) {
        return <span className="text-slate-400" data-testid={`location-none-${entry.id}`}>—</span>;
    }
    return (
        <div className="flex items-center gap-1.5">
            {inGps ? (
                <a
                    href={mapsUrl(entry.clock_in_gps)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-0.5 rounded-full border border-[#0066D9]/30 bg-[#0066D9]/5 px-2 py-0.5 text-[11px] font-semibold text-[#0066D9] hover:bg-[#0066D9]/15 transition-colors"
                    title="Open Clock In location in Google Maps"
                    data-testid={`location-in-${entry.id}`}
                >
                    <MapPin size={12} weight="fill" /> IN
                </a>
            ) : (
                <span className="text-slate-300 text-[11px]">IN —</span>
            )}
            {outGps ? (
                <a
                    href={mapsUrl(entry.clock_out_gps)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-0.5 rounded-full border border-emerald-600/30 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors"
                    title="Open Clock Out location in Google Maps"
                    data-testid={`location-out-${entry.id}`}
                >
                    <MapPin size={12} weight="fill" /> OUT
                </a>
            ) : (
                entry.status === "completed" && <span className="text-slate-300 text-[11px]">OUT —</span>
            )}
        </div>
    );
};

const GpsBlock = ({ label, gps, testid }) => (
    <div className="rounded-lg border border-slate-200 p-3.5 space-y-2" data-testid={testid}>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        {hasGps(gps) ? (
            <>
                <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Latitude</div>
                        <div className="font-mono text-[#003366]">{gps.lat.toFixed(6)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Longitude</div>
                        <div className="font-mono text-[#003366]">{gps.lng.toFixed(6)}</div>
                    </div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">GPS accuracy</div>
                    <div className="font-mono text-sm text-[#003366]">
                        {typeof gps.accuracy === "number" ? `± ${Math.round(gps.accuracy)} m` : "Not recorded"}
                    </div>
                </div>
                <Button
                    asChild
                    variant="outline"
                    className="h-9 w-full border-[#0066D9] text-[#0066D9] hover:bg-[#0066D9]/5"
                >
                    <a href={mapsUrl(gps)} target="_blank" rel="noreferrer" data-testid={`${testid}-maps-button`}>
                        <MapTrifold size={16} className="mr-1.5" /> View on Google Maps
                    </a>
                </Button>
            </>
        ) : (
            <div className="flex items-center gap-1.5 text-sm text-amber-700">
                <Warning size={15} /> Location unavailable for this event
            </div>
        )}
    </div>
);

export const TimeRecordDetailDialog = ({ entry, open, onOpenChange, onEdit, onCloseShift }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="time-record-detail-dialog">
            <DialogHeader>
                <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                    {entry?.technician_name}
                </DialogTitle>
                <DialogDescription>
                    {formatWorkDate(entry?.work_date)} · {entry?.site_name}
                    {entry?.sublocation_name ? ` · ${entry.sublocation_name}` : ""}
                </DialogDescription>
            </DialogHeader>

            {entry && (
                <div className="space-y-4">
                    {entry.needs_review && (
                        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
                            data-testid="detail-needs-review-warning">
                            <Warning size={18} weight="fill" />
                            <span>Open {formatSeconds(entry.open_seconds)} — needs review. This shift was never closed.</span>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                Clock In ({COMPANY_TZ_LABEL})
                            </div>
                            <div className="text-lg font-bold text-[#003366]">{formatClockTime(entry.clock_in_local)}</div>
                        </div>
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                Clock Out ({COMPANY_TZ_LABEL})
                            </div>
                            <div className="text-lg font-bold text-[#003366]">
                                {entry.clock_out_local ? formatClockTime(entry.clock_out_local) : "—"}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <Badge className={entry.status === "active"
                            ? "bg-[#0066D9] text-white text-[10px] uppercase tracking-wider"
                            : "bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] uppercase tracking-wider"}>
                            {entry.status}
                        </Badge>
                        {entry.elapsed_display && (
                            <span className="text-slate-600">Elapsed: <strong>{entry.elapsed_display}</strong></span>
                        )}
                        {entry.job_code && (
                            <span className="text-slate-600 font-mono text-xs">Job code: {entry.job_code}</span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <GpsBlock label="Clock In location" gps={entry.clock_in_gps} testid="detail-gps-in" />
                        <GpsBlock label="Clock Out location" gps={entry.clock_out_gps} testid="detail-gps-out" />
                    </div>

                    {entry.notes && (
                        <div className="text-sm text-slate-600">
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block">Admin notes</span>
                            {entry.notes}
                        </div>
                    )}

                    {(entry.audit_trail?.length || 0) > 0 && (
                        <div className="space-y-2">
                            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                                Audit trail
                            </div>
                            {entry.audit_trail.map((a, i) => (
                                <div key={`${a.changed_at}-${a.field}`} className="rounded-lg border border-slate-200 p-2.5 text-xs" data-testid={`detail-audit-${i}`}>
                                    <span className="font-semibold text-[#003366]">{a.field}: </span>
                                    <span className="line-through text-slate-400">{formatAuditValue(a.old_value)}</span>
                                    {" → "}
                                    <span className="font-medium">{formatAuditValue(a.new_value)}</span>
                                    <div className="text-slate-400 mt-0.5">
                                        {a.admin_name} · {a.changed_at?.slice(0, 16).replace("T", " ")} UTC
                                        {a.reason ? ` · ${a.reason}` : ""}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {(onEdit || onCloseShift) && (
                <DialogFooter className="gap-2 sm:gap-0" data-testid="detail-admin-actions">
                    {onCloseShift && entry?.status === "active" && (
                        <Button
                            onClick={() => onCloseShift(entry)}
                            className="bg-[#E6002C] hover:bg-[#c30025] text-white font-semibold"
                            data-testid="detail-close-shift-button"
                        >
                            <LockKey size={16} className="mr-1.5" /> Close shift
                        </Button>
                    )}
                    {onEdit && (
                        <Button
                            onClick={() => onEdit(entry)}
                            variant={onCloseShift && entry?.status === "active" ? "outline" : "default"}
                            className={
                                onCloseShift && entry?.status === "active"
                                    ? "border-[#0066D9] text-[#0066D9] hover:bg-[#0066D9]/5"
                                    : "bg-[#0066D9] hover:bg-[#0052ad] text-white"
                            }
                            data-testid="detail-edit-button"
                        >
                            <PencilSimple size={16} className="mr-1.5" /> Edit shift
                        </Button>
                    )}
                </DialogFooter>
            )}
        </DialogContent>
    </Dialog>
);
